import type { PaymentMethod } from '@/scopes/common/domain/payment-methods/payment-methods'
import type { AnalyticsClient, Logger, SiteName } from '@/scopes/common/domain/types'
import type { LocationResolver } from '@/scopes/common/infrastructure/dom/location'
import ga4analyticsClient from '@/scopes/common/infrastructure/analytics/ga4analyticsClient'
import { createPaymentApiClient } from '../infrastructure/paymentApiClient'
import { paymentStore } from '../infrastructure/store'
import { createDownPaymentCoveredEvent, createPaymentEvent, getPaymentEventContext } from './analytics'
import { isFullOnlyPayment } from './policies/fullOnlyPayment'
import { getPaymentMethodsPolicyTransformer } from './policies/paymentMethodsAvailability'
import { buildPaymentMethodRows } from './policies/paymentMethodsView'
import { parsePaymentResultFromQueryString } from './policies/paymentResult'
import { getRebookingPaymentStatus } from './policies/rebookingPaymentStatus'
import { AdyenPaymentStatusValues, AmountType, AmountTypeValues, PaymentLoadStatusValues, PaymentScopePage, PaymentScopePageValues, RebookingPaymentStatusValues } from './types'

interface PaymentActionsDependencies {
  apiClient: ReturnType<typeof createPaymentApiClient>
  store: typeof paymentStore
  location: LocationResolver
  logger: Logger
  siteName: SiteName
  scope?: PaymentScopePage
  bookingNumber: string
  resetVoucher: () => void
  analyticsClient?: AnalyticsClient
  goToAmounts?: () => void
  goToMethods?: () => void
}

export function createPaymentActions({
  apiClient,
  store,
  location,
  logger,
  siteName,
  scope,
  bookingNumber,
  resetVoucher,
  analyticsClient = ga4analyticsClient,
  goToAmounts,
  goToMethods,
}: PaymentActionsDependencies) {
  const context = getPaymentEventContext(scope ?? 'BookingPayment')
  let coveredPaymentEventSent = false
  const sentPaymentResults = new Set<string>()
  const sentPaymentErrors = new Set<string>()
  const sentUnavailableMethodSelections = new Set<string>()
  const sentUnavailableMethodLists = new Set<string>()

  function sendPaymentResult(paymentResult: string) {
    if (sentPaymentResults.has(paymentResult))
      return

    analyticsClient.sendEvent(createPaymentEvent({
      description: 'payment result',
      element: 'payment result banner',
      type: 'redirect',
      result: paymentResult,
      interaction: false,
      context,
    }))
    sentPaymentResults.add(paymentResult)
  }

  function sendPaymentError(type: 'configuration' | 'load', result: string) {
    const eventKey = `${type}:${result}`
    if (sentPaymentErrors.has(eventKey))
      return

    analyticsClient.sendEvent(createPaymentEvent({
      description: 'payment error',
      element: 'payment component',
      type,
      result,
      interaction: false,
      context,
    }))
    sentPaymentErrors.add(eventKey)
  }

  async function pageLoaded() {
    const state = store.getState()
    const paymentResult = parsePaymentResultFromQueryString(location.search())
    resetVoucher()

    if (!bookingNumber) {
      state.setIn('loadStatus', PaymentLoadStatusValues.ERROR)
      sendPaymentError('configuration', 'booking number missing')
      return
    }

    state.setIn('loadStatus', PaymentLoadStatusValues.LOADING)

    try {
      const paymentOptions = await apiClient.getPaymentOptions(bookingNumber)

      state.initialize({
        paymentOptions,
        selectedAmountType: isFullOnlyPayment(paymentOptions.paymentAmounts) ? AmountTypeValues.FULL : '',
        selectedPaymentMethodType: null,
        customDepositAmount: null,
        loadStatus: PaymentLoadStatusValues.SUCCESS,
        status: AdyenPaymentStatusValues.IDLE,
        paymentResult,
        successUrl: null,
      })

      if (paymentResult) {
        sendPaymentResult(paymentResult)
      }

      if (paymentOptions.downPaymentAlreadyCovered && !coveredPaymentEventSent) {
        analyticsClient.sendEvent(createDownPaymentCoveredEvent({
          element: 'alert',
          type: 'payment',
          result: 'pending confirmation',
          interaction: false,
        }))
        coveredPaymentEventSent = true
      }
    }
    catch (error) {
      state.setIn('loadStatus', PaymentLoadStatusValues.ERROR)
      logger.logError(error instanceof Error ? error : new Error(String(error)), { action: 'pageLoaded' })
      sendPaymentError('load', 'payment options unavailable')
    }
  }

  function selectAmountType(amountType: AmountType) {
    const state = store.getState()
    state.setIn('selectedAmountType', amountType)
    if (amountType !== AmountTypeValues.DEPOSIT) {
      state.setIn('customDepositAmount', null)
    }

    const paymentOptions = state.paymentOptions
    if (!paymentOptions) {
      state.setIn('selectedPaymentMethodType', null)
      return
    }

    const selectedAmount = paymentOptions.paymentAmounts.find(amount => amount.type === amountType)
    if (!selectedAmount) {
      state.setIn('selectedPaymentMethodType', null)
      return
    }

    analyticsClient.sendEvent(
      createPaymentEvent({
        description: 'set payment amount',
        element: 'radio button',
        type: 'payment_amount',
        result: selectedAmount.type,
        interaction: true,
        context,
        action: 'amount_selection',
      }),
    )

    const availablePaymentMethods = buildPaymentMethodRows(
      paymentOptions.paymentMethods,
      selectedAmount.availablePaymentMethodTypes,
    )
    const availablePaymentMethodsBySite = getPaymentMethodsPolicyTransformer(siteName)(availablePaymentMethods)

    if (!availablePaymentMethodsBySite.some(method => method.key === state.selectedPaymentMethodType)) {
      state.setIn('selectedPaymentMethodType', null)
    }
  }

  function setCustomDepositAmount(amount: number) {
    const state = store.getState()
    state.setIn('customDepositAmount', amount)
    analyticsClient.sendEvent(createPaymentEvent({
      description: 'set custom partial payment amount',
      element: 'input',
      type: 'payment_amount',
      result: 'custom_partial',
      interaction: true,
      context,
      action: 'amount_selection',
    }))
  }

  function paymentMethodSelected(selectedPaymentMethodType: PaymentMethod | null) {
    const state = store.getState()

    if (selectedPaymentMethodType) {
      state.setIn('paymentResult', null)
      analyticsClient.sendEvent(
        createPaymentEvent({
          description: 'set payment method',
          element: 'radio button',
          type: 'payment_method',
          result: selectedPaymentMethodType,
          interaction: true,
          context,
          action: 'method_selection',
        }),
      )
    }

    state.setIn('selectedPaymentMethodType', selectedPaymentMethodType)
  }

  function paymentMethodBecameUnavailable(paymentMethodType: PaymentMethod) {
    const state = store.getState()
    const eventKey = `${paymentMethodType}:${state.selectedAmountType}:${state.customDepositAmount ?? ''}`

    state.setIn('selectedPaymentMethodType', null)

    if (sentUnavailableMethodSelections.has(eventKey))
      return

    analyticsClient.sendEvent(createPaymentEvent({
      description: 'payment method unavailable',
      element: 'payment method',
      type: paymentMethodType,
      result: 'custom amount not supported',
      interaction: false,
      context,
    }))
    sentUnavailableMethodSelections.add(eventKey)
  }

  function paymentMethodsBecameUnavailable(amountType: AmountType) {
    if (sentUnavailableMethodLists.has(amountType))
      return

    analyticsClient.sendEvent(createPaymentEvent({
      description: 'payment methods unavailable',
      element: 'payment methods',
      type: amountType,
      result: 'no methods available',
      interaction: false,
      context,
    }))
    sentUnavailableMethodLists.add(amountType)
  }

  function amountsStepRequested() {
    analyticsClient.sendEvent(createPaymentEvent({
      description: 'payment step navigation',
      element: 'button',
      type: 'payment_amount',
      result: 'amounts',
      interaction: true,
      context,
    }))
    if (goToAmounts) {
      goToAmounts()
    }
  }

  function methodsStepRequested(selectedAmountType: AmountType) {
    analyticsClient.sendEvent(
      createPaymentEvent({
        description: 'set payment amount',
        element: 'button',
        type: 'payment_amount',
        result: selectedAmountType,
        interaction: true,
        context,
        action: 'amount_confirmation',
      }),
    )
    if (goToMethods) {
      goToMethods()
    }
  }

  function nextStepRequested(nextStepUrl: string) {
    const state = store.getState()

    try {
      if (scope !== PaymentScopePageValues.MANDATORY_PAYMENT || state.loadStatus !== PaymentLoadStatusValues.SUCCESS || !state.paymentOptions) {
        throw new Error('Payment options must be loaded on the mandatory payment page before continuing')
      }

      const paymentStatus = getRebookingPaymentStatus(state.paymentOptions)
      if (paymentStatus !== RebookingPaymentStatusValues.FULLY_COVERED && paymentStatus !== RebookingPaymentStatusValues.DOWN_PAYMENT_COVERED) {
        throw new Error('The required payment must be covered before continuing')
      }

      if (!nextStepUrl.trim() || !bookingNumber) {
        throw new Error('Next step URL or checkout session is missing')
      }

      const url = new URL(nextStepUrl, location.origin())
      if (url.origin !== location.origin()) {
        throw new Error('Invalid next step URL returned by the server')
      }
      url.searchParams.set('sid', bookingNumber)

      analyticsClient.sendEvent(createPaymentEvent({
        description: 'payment step navigation',
        element: 'button',
        type: 'next_step',
        result: paymentStatus,
        interaction: true,
        context,
      }))
      location.redirectTo(url.toString())
    }
    catch {
      state.setIn('loadStatus', PaymentLoadStatusValues.ERROR)
      logger.logError(
        new Error('Failed to continue the rebooking checkout: invalid payment state or next step configuration'),
        { action: 'nextStepRequested' },
      )
      sendPaymentError('configuration', 'next step unavailable')
    }
  }

  return {
    pageLoaded,
    selectAmountType,
    paymentMethodSelected,
    paymentMethodBecameUnavailable,
    paymentMethodsBecameUnavailable,
    setCustomDepositAmount,
    amountsStepRequested,
    methodsStepRequested,
    nextStepRequested,
  }
}

export type PaymentActions = ReturnType<typeof createPaymentActions>
